import { plpSpecificObjects } from '@finn/auto-ui/components/CosmicHelperWrapper/pageSpecificObjects';
import PartnerVoucherBanner from '@finn/auto-ui/components/PartnerVoucherBanner';
import { CosmicContent } from '@finn/auto-ui/components/PLPAdvisories';
import CheckoutContext from '@finn/auto-ui/contexts/Checkout';
import { useClosedUserGroup } from '@finn/auto-ui/contexts/ClosedUserGroup';
import { useGetUSPBanner } from '@finn/auto-ui/hooks/useGetUSPBanner';
import { useLoyaltyAndSubscriptionTracker } from '@finn/auto-ui/modules/checkout/components/Pages/UnifiedCheckout/CheckoutAside/useLoyaltyAndSubscriptionTracker';
import { RecommendationSection } from '@finn/auto-ui/modules/recommendation/components/RecommendationSection';
import ProductSlider from '@finn/auto-ui/ui-modules/ProductSlider';
import { getCachedCosmicObject } from '@finn/auto-ui/utils/cosmic';
import { buildFAQJsonLdAsStrForPage } from '@finn/auto-ui/utils/html';
import { useHandleModelCardSelection } from '@finn/auto-ui/utils/plp';
import {
  getBrandProductSchema,
  getPLPContent,
  handleProductList,
} from '@finn/auto-ui/utils/plp-ssr';
import {
  CosmicHelperWrapper,
  SpacingWrapper,
  useDeals,
} from '@finn/platform-modules';
import { BASE_SUBSCRIPTION_PATH } from '@finn/ua-constants';
import {
  AvailableFilters,
  CombinedFilters,
  CombinedFiltersTags,
  createProductFetchers,
  DiscoveryConfigProvider,
  FilterContext,
  FilterKey,
  FilterProvider,
  FILTERS_RESPONSE_SWR_FALLBACK_KEY,
  FiltersType,
  getNoIndexNoFollow,
  getPageSize,
  GetVehiclesResponse,
  PathFilterProvider,
  ProductListing,
  TopCampaignBanner,
  useAvailableFiltersForState,
  useFiltersForProducts,
  useFilterValues,
  useIsOutOfStock,
  usePartnerDiscount,
  useProducts,
  useReplaceFilterValues,
  VehicleViewKey,
} from '@finn/ua-vehicle';
import { USPBannerData } from '@finn/ui-cosmic';
import {
  cn,
  config,
  FakeDefaultLocale,
  getLocaleFromContext,
  Locale,
} from '@finn/ui-utils';
import get from 'lodash/get';
import { GetStaticPaths, GetStaticProps, NextPage } from 'next';
import { useRouter } from 'next/router';
import { ReactNode, useContext, useMemo, useRef } from 'react';
import { SWRConfig } from 'swr';

import { AlertMessage } from '~/components/Alerts';
import ErrorPage from '~/components/ErrorPage';
import HeadTags from '~/components/HeadTags';
import MetaContent from '~/components/MetaContent';
import useGetContentPieces from '~/components/ProductListingPage/useGetContentPieces';
import useOnPLPMount from '~/components/ProductListingPage/useOnPLPMount';
import Page from '~/layouts/Default';
import availableFilters from '~/localization/available.filters.json';
import displayedFilters from '~/localization/displayed.filters.json';
import { FiltersTracking } from '~/modules/products/filters';
import { BaseLandingPage, SeoHeaderData } from '~/types/cosmicModules';
import { IErrorPage } from '~/types/general';
import {
  AdjustedProductListResults,
  IPropsProductListingPage,
} from '~/types/productListing';
import { StickyNavContent } from '~/ui-modules/StickyNav/types';

interface IStickyNavContentProp {
  stickyNavContent: StickyNavContent;
}

interface ICommonProps extends BaseLandingPage {
  plpLd?: string;
  productListItemsResponse?: GetVehiclesResponse;
  nextAvailableFilters: AvailableFilters;
  seoHeader: SeoHeaderData;
  renderMetaContent?: (
    pageData: BaseLandingPage['pageData'],
    { uspBanner }: { uspBanner: USPBannerData }
  ) => ReactNode;
}

export type Props = ICommonProps &
  IErrorPage &
  IPropsProductListingPage &
  IStickyNavContentProp;

// TODO temporary extracted in separate component
// to provide access to context, will be removed after context removed
const PLPCore = ({
  productListItemsResponse,
  pageData,
  isOutOfStock,
}: Partial<ICommonProps> & { isOutOfStock?: boolean }) => {
  const { closedUserGroupId } = useClosedUserGroup();
  const { cartInfo } = useContext(CheckoutContext);
  const partnerDiscount = usePartnerDiscount({
    closedUserGroupId,
    cartInfo,
  });
  const gridRef = useRef<HTMLDivElement | null>(null);
  useLoyaltyAndSubscriptionTracker('Product Listing');
  const { deals: subscriptions } = useDeals();
  const filtersForProductsToFetch = useFiltersForProducts({
    subscriptions,
    initialFilters: isOutOfStock
      ? {
          [FilterKey.VIEW]: VehicleViewKey.DISPLAYED_CARS,
        }
      : undefined,
  });

  const { filters, products, status, total, limit, loadNextPage, page } =
    useProducts({
      fallbackData: productListItemsResponse,
      infinite: !isOutOfStock,
      limit: getPageSize(
        isOutOfStock,
        pageData.metadata.campaign_cards?.length
      ),
      gridRef,
      filters: filtersForProductsToFetch,
    });

  const filterValues = useFilterValues();
  const availableFiltersDynamic = useAvailableFiltersForState(filterValues);
  const isOutOfStockDynamic = useIsOutOfStock();
  const onModelCardSelection = useHandleModelCardSelection();
  const replaceFilterValues = useReplaceFilterValues();

  return (
    <div ref={gridRef} data-testid="product-listing">
      <RecommendationSection filters={filters} />
      <FiltersTracking total={total} />
      <ProductListing
        status={status}
        partnerDiscount={partnerDiscount}
        campaignData={pageData.metadata.campaign_cards}
        items={products}
        paginationType={isOutOfStock ? 'pagination' : 'infinite'}
        page={page}
        total={total}
        limit={limit}
        fetchNextPage={loadNextPage}
        filterValues={filterValues}
        availableFilters={availableFiltersDynamic}
        isOutOfStock={isOutOfStockDynamic}
        onMessage={(type, ...args) => {
          if (type === 'model_card_selected') {
            onModelCardSelection(...args);
          }
          if (type === 'reset_filters') {
            replaceFilterValues({});
          }
        }}
      />
    </div>
  );
};

const SeoRelevantPLP: NextPage<Props> = ({
  plpLd,
  isOutOfStock,
  productListItemsResponse,
  nextAvailableFilters,
  allAvailableFilters,
  vehicles,
  pageData,
  initialFilter,
  seoHeader,
  totalVehiclesCount,
  headline,
  advisoryContentObject,
  statusCode,
  errorMessage,
  isMobile,
  canonicalUrl,
  isNoIndexNoFollow,
  renderMetaContent,
}) => {
  const router = useRouter();
  const jsonLdArray = [];
  const jsonldAsString =
    advisoryContentObject &&
    buildFAQJsonLdAsStrForPage(advisoryContentObject.metadata.faq?.questions);

  if (jsonldAsString) {
    jsonLdArray.push(jsonldAsString);
  }
  if (plpLd) {
    jsonLdArray.push(plpLd);
  }

  const uspBanner = useGetUSPBanner({
    pageData,
    initialFilter,
    totalVehiclesCount,
    nextAvailableFilters,
    allAvailableFilters,
    shouldConsiderAsLandingPageProp: false,
  });

  const topCampaignBanner =
    pageData?.metadata?.promo_banners?.top_campaign_banner_test;

  const { isCarNotExist, isRedirectedPageNotExist, isDealClosed } =
    useOnPLPMount();

  const { alertMessage, labels } = useGetContentPieces({
    pageData,
    isRedirectedPageNotExist,
    isDealClosed,
  });

  const swrConfigValue = useMemo(() => {
    return {
      fallback: {
        [FILTERS_RESPONSE_SWR_FALLBACK_KEY]: allAvailableFilters,
      },
    };
  }, [allAvailableFilters]);

  const { deals } = useDeals();

  if (statusCode) {
    return (
      <ErrorPage
        pageData={pageData}
        statusCode={statusCode}
        error={new Error(errorMessage)}
      />
    );
  }

  return (
    <SWRConfig value={swrConfigValue}>
      <PathFilterProvider
        basePathname={BASE_SUBSCRIPTION_PATH}
        isOutOfStock={isOutOfStock}
      >
        <CosmicHelperWrapper
          data={pageData}
          preview={false}
          localizationObjects={plpSpecificObjects}
        />
        {(isCarNotExist || isRedirectedPageNotExist || isDealClosed) && (
          <AlertMessage message={alertMessage} />
        )}

        <FilterProvider
          allAvailableFilters={allAvailableFilters}
          nextAvailableFilters={nextAvailableFilters}
          pageData={pageData}
          vehicles={vehicles}
          vehiclesOffset={productListItemsResponse.offset}
          labelsData={labels}
          totalVehiclesCount={totalVehiclesCount}
          advisoryContentObject={advisoryContentObject}
          seoHeader={seoHeader}
          headline={headline}
          initialFilter={initialFilter}
        >
          <FilterContext.Consumer>
            {({
              filterSEOHeader,
              canonicalUrl: canonicalUrlFE,
              isNoIndexNoFollow: isNoIndexNoFollowFE,
            }) => (
              <Page data={pageData} isMobile={isMobile}>
                <HeadTags
                  seoHeader={filterSEOHeader}
                  isNoIndexNoFollow={isNoIndexNoFollowFE || isNoIndexNoFollow} // && isOutOfStock}
                  canonicalUrl={canonicalUrlFE || canonicalUrl}
                  jsonLdContent={jsonLdArray}
                />

                <div className="mt-4">
                  <div className="container md:flex">
                    <div
                      className={cn('w-full', {
                        'flex flex-col': !isOutOfStock,
                      })}
                    >
                      <PartnerVoucherBanner className="mb-2" />
                      <MetaContent
                        renderMetaContent={renderMetaContent}
                        pageData={pageData}
                        uspBanner={uspBanner}
                        isOutOfStock={isOutOfStock}
                      />
                      <div
                        className={cn({
                          'md:flex': !isOutOfStock,
                        })}
                      >
                        <DiscoveryConfigProvider
                          router={router}
                          deals={deals}
                          filterOverrides={{
                            power: {
                              hidden: true,
                            },
                          }}
                        >
                          {!isOutOfStock ? (
                            <>
                              <CombinedFilters />
                              <div className="container">
                                <CombinedFiltersTags />
                                <TopCampaignBanner
                                  topCampaignBanner={topCampaignBanner}
                                />
                                <PLPCore
                                  isOutOfStock={isOutOfStock}
                                  pageData={pageData}
                                  productListItemsResponse={
                                    productListItemsResponse
                                  }
                                />
                              </div>
                            </>
                          ) : (
                            <div className="container">
                              <PLPCore
                                isOutOfStock={isOutOfStock}
                                pageData={pageData}
                                productListItemsResponse={
                                  productListItemsResponse
                                }
                              />
                            </div>
                          )}
                        </DiscoveryConfigProvider>
                      </div>
                    </div>
                  </div>
                  {isOutOfStock && (
                    <div className="container">
                      <SpacingWrapper />
                      <ProductSlider
                        title={get(
                          pageData,
                          'metadata.vehicles_slider_title',
                          ''
                        )}
                        isInPLP
                      />
                    </div>
                  )}
                  {advisoryContentObject && (
                    <CosmicContent
                      cosmicObject={advisoryContentObject}
                      nextAvailableFilters={nextAvailableFilters}
                    />
                  )}
                </div>
              </Page>
            )}
          </FilterContext.Consumer>
        </FilterProvider>
      </PathFilterProvider>
    </SWRConfig>
  );
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  try {
    const slugArray = Array.isArray(ctx?.params?.slug) ? ctx?.params?.slug : [];

    slugArray.unshift(BASE_SUBSCRIPTION_PATH);

    const locale = getLocaleFromContext(ctx);

    const localePrefix = locale !== FakeDefaultLocale ? `${locale}` : '';
    const canonicalUrl = `${
      config.FINN_MAIN_DOMAIN
    }/${localePrefix}/${slugArray.join('/')}`;

    if (ctx?.params?.slug?.length > 3) {
      return {
        notFound: true,
      };
    }

    const pageData = await getCachedCosmicObject({
      slug: 'static-plp',
      locale,
    });

    const {
      productListItemsResponse,
      isOutOfStock,
      filterValues,
      filtersResponse,
    } = await createProductFetchers().fetchProductItems({
      locale,
      slugArray,
      availableFilters: availableFilters as unknown as FiltersType,
      displayedFilters: displayedFilters as unknown as FiltersType,
      pageData,
    });

    const { results: vehicles, total_results: totalResults } =
      productListItemsResponse;

    /* if brand is in stock but filtered vehicles are out of stock, isOutOfStock still returns false
    because it is not aware of filter combinations beyond brand and model;
    therefore double-check vehicles response with out-of-stock view before redirecting */

    const adjustedProductList = (await handleProductList(
      locale,
      totalResults,
      isOutOfStock,
      localePrefix,
      filterValues,
      pageData
    )) as AdjustedProductListResults;

    const availableTotalResults =
      adjustedProductList?.adjustedTotalResults || totalResults;
    const availableVehicles = adjustedProductList?.adjustedVehicles || vehicles;

    const plpContent = await getPLPContent({
      isOutOfStock: adjustedProductList?.adjustedIsOutOfStock || isOutOfStock,
      filtersResponse,
      filterValues,
      totalResults: availableTotalResults,
      locale,
    });

    const outOfStock =
      adjustedProductList?.adjustedIsOutOfStock || isOutOfStock;

    const plpLd = await getBrandProductSchema({
      locale,
      isOutOfStock: outOfStock,
      filterValues,
      availableFilters: plpContent.nextAvailableFilters,
    });

    return {
      props: {
        plpLd,
        isOutOfStock: outOfStock,
        productListItemsResponse,
        vehicles: availableVehicles || {},
        nextAvailableFilters: plpContent.nextAvailableFilters || {},
        initialFilter: filterValues,
        pageData: plpContent.pageData || {},
        seoHeader: plpContent.seoHeader,
        headline: plpContent.headline,
        totalVehiclesCount: availableTotalResults,
        advisoryContentObject: plpContent.advisoryContentObject || null,
        allAvailableFilters: filtersResponse,
        canonicalUrl,
        isNoIndexNoFollow: getNoIndexNoFollow(
          filterValues,
          slugArray.join('/'),
          availableVehicles.length
        ),
        // add a new param to see if that invalidates the cache
        version: 2,
      },
      // reduce revalidation time to 60 seconds
      revalidate: config.REVALIDATION_SHORT_TIMEOUT,
    };
  } catch (e) {
    console.error(e);

    return {
      notFound: true,
      revalidate: config.REVALIDATION_SHORT_TIMEOUT,
    };
  }
};

export const getStaticPaths: GetStaticPaths = async (ctx) => {
  const locales = ctx.locales?.length
    ? ctx.locales.filter((locale) => locale !== FakeDefaultLocale)
    : [Locale.GERMAN_GERMANY, Locale.ENGLISH_USA];
  const paths = [];

  locales.forEach((locale) => {
    paths.push({
      params: {
        slug: [BASE_SUBSCRIPTION_PATH],
        locale,
      },
    });
  });

  return {
    paths,
    fallback: 'blocking',
  };
};

export default SeoRelevantPLP;
